import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Typography, Alert,
} from '@mui/material';

export interface QuickPayTarget {
  billId: string;
  payeeName: string;
  /** The amount quick pay will use, when known: the statement, else an estimate. */
  amount?: string | null;
}

interface Props {
  target: QuickPayTarget | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (billId: string, payeeName: string, input: { amount?: string; payment_fee?: string }) => void;
}

/** Round to cents. */
const cents = (n: number) => (Math.round(n * 100) / 100).toFixed(2);

/**
 * Quick pay, plus what it cost to pay.
 *
 * A utility paid by credit card through a processor carries a fee (1.49% is
 * typical). Plain Quick pay had no way to record it, so the fee meant a second
 * trip through the full payment dialog. This keeps every Quick pay default --
 * same account, same rail -- and adds the one number that changes.
 *
 * The fee can be typed as dollars or as a percentage of the amount; the
 * percentage is only a calculator, and dollars are what is saved.
 */
export const QuickPayFeeDialog: React.FC<Props> = ({ target, busy, onClose, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState('');
  const [rate, setRate] = useState('');

  useEffect(() => {
    setAmount(target?.amount ?? '');
    setFee('');
    setRate('');
  }, [target]);

  const applyRate = (r: string) => {
    setRate(r);
    const pct = Number(r);
    const amt = Number(amount);
    if (pct > 0 && amt > 0) setFee(cents((amt * pct) / 100));
  };

  return (
    <Dialog open={!!target} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Quick pay with fee</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {target?.payeeName} · same account and payment method as last time.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Amount" size="small" fullWidth value={amount}
            onChange={(e) => setAmount(e.target.value)}
            helperText="Leave as is to use the statement amount"
          />
          <Stack direction="row" spacing={1}>
            <TextField
              label="Fee %" size="small" value={rate}
              onChange={(e) => applyRate(e.target.value)}
              sx={{ width: 110 }}
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              label="Payment fee" size="small" fullWidth value={fee}
              onChange={(e) => { setFee(e.target.value); setRate(''); }}
              helperText="Card processing, Chexy. Not added to the bill."
              inputProps={{ inputMode: 'decimal' }}
            />
          </Stack>
          <Alert severity="info" sx={{ py: 0.5 }}>
            Records a payment you already made. No money is moved.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained" disabled={busy || !target}
          onClick={() => target && onSubmit(target.billId, target.payeeName, {
            amount: amount && amount !== (target.amount ?? '') ? amount : undefined,
            payment_fee: fee || undefined,
          })}
        >
          {busy ? 'Recording…' : 'Quick pay'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
